import { RadialBarChart, RadialBar, PolarAngleAxis, ResponsiveContainer } from "recharts";
import { BadgeCheck } from "lucide-react";
import { Editable } from "@/context/ContentContext";

export function RatedBadge({ dupr, vair }) {
  if (!dupr && !vair) return null;
  return (
    <span className="inline-flex items-center gap-1 ml-1.5 align-middle" data-testid="rated-badge">
      {dupr && (
        <span title={`DUPR ${Number(dupr).toFixed(2)} — verified external rating`}
          className="inline-flex items-center gap-0.5 text-[8px] font-bold uppercase tracking-widest text-pb-cyan border border-pb-cyan/40 bg-pb-cyan/10 px-1 py-px">
          <BadgeCheck className="w-2.5 h-2.5" />DUPR
        </span>
      )}
      {vair && (
        <span title={`VAIR ${Number(vair).toFixed(2)} — verified external rating`}
          className="inline-flex items-center gap-0.5 text-[8px] font-bold uppercase tracking-widest text-amber-400 border border-amber-400/40 bg-amber-400/10 px-1 py-px">
          <BadgeCheck className="w-2.5 h-2.5" />VAIR
        </span>
      )}
    </span>
  );
}

export function PageHeader({ kicker, title, subtitle, ck, children }) {
  const txt = (suffix, val, Tag, cls) =>
    ck && typeof val === "string"
      ? <Editable ck={`${ck}.${suffix}`} fallback={val} as={Tag} className={cls} />
      : <Tag className={cls}>{val}</Tag>;
  return (
    <div className="flex flex-wrap items-end justify-between gap-4 mb-8 animate-fade-up">
      <div>
        {kicker && txt("kicker", kicker, "div", "text-xs font-bold uppercase tracking-[0.3em] text-pb-cyan mb-2")}
        {txt("title", title, "h1", "font-display font-black uppercase tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-none")}
        {subtitle && txt("subtitle", subtitle, "p", "text-zinc-400 mt-3 max-w-2xl text-sm md:text-base")}
      </div>
      {children}
    </div>
  );
}

export function StatCard({ label, value, unit, accent = "#A3E635", testid }) {
  return (
    <div data-testid={testid} className="bg-pb-surface border border-pb-border p-5 hover:-translate-y-1 hover:border-pb-red transition-all">
      <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 mb-2">{label}</div>
      <div className="flex items-baseline gap-1">
        <span className="font-display font-black text-3xl md:text-4xl" style={{ color: accent }}>{value}</span>
        {unit && <span className="text-xs text-zinc-500 font-mono">{unit}</span>}
      </div>
    </div>
  );
}

export function RadialGauge({ value = 0, label, color = "#A3E635", size = 160 }) {
  const data = [{ value: Math.max(0, Math.min(100, value)) }];
  return (
    <div className="relative flex flex-col items-center" style={{ width: size }}>
      <ResponsiveContainer width={size} height={size}>
        <RadialBarChart innerRadius="72%" outerRadius="100%" data={data} startAngle={90} endAngle={-270}>
          <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
          <RadialBar background={{ fill: "#1A1A1A" }} dataKey="value" cornerRadius={0} fill={color} />
        </RadialBarChart>
      </ResponsiveContainer>
      <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
        <span className="font-display font-black text-3xl" style={{ color }}>{value}</span>
        {label && <span className="text-[9px] uppercase tracking-[0.2em] text-zinc-500 mt-1">{label}</span>}
      </div>
    </div>
  );
}

export function Pill({ children, color = "#A3E635" }) {
  return (
    <span className="inline-block px-2 py-1 text-[10px] font-bold uppercase tracking-widest border"
      style={{ color, borderColor: `${color}40`, backgroundColor: `${color}1a` }}>
      {children}
    </span>
  );
}

export function AiCard({ children, loading }) {
  return (
    <div className="relative bg-gradient-to-br from-[#141414] to-[#0d0d0d] border border-pb-cyan/30 p-5 overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-pb-cyan via-pb-red to-transparent" />
      <div className="flex items-center gap-2 mb-3">
        <span className="w-2 h-2 bg-pb-cyan animate-pulse" />
        <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-pb-cyan">Emergent AI Engine</span>
      </div>
      {loading ? <div className="text-zinc-500 text-sm animate-pulse">Analyzing performance data…</div> : children}
    </div>
  );
}
